import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft, Play, Grid3X3, List, Plus, Film, GripVertical, Edit3,
  Send, Loader2, CheckCircle2, AlertTriangle, Star, Filter, Flag
} from "lucide-react";
import GuidanceModal from "@/components/GuidanceModal";

import { getApiUrl, getApiBaseUrl } from '@/config/api';

const API = getApiUrl();

// Status configuration
const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-zinc-600", textColor: "text-zinc-300" },
  ready: { label: "Ready", color: "bg-blue-600", textColor: "text-blue-300", hidden: true },
  animating: { label: "Animating", color: "bg-amber-500", textColor: "text-amber-300", spinner: true },
  animated: { label: "Animated", color: "bg-green-600", textColor: "text-green-300" },
  needs_revision: { label: "Revision", color: "bg-orange-500", textColor: "text-orange-300" },
  final: { label: "Final", color: "bg-yellow-500", textColor: "text-yellow-300", icon: Star }
};

const getAssetUrl = (fileUrl) => {
  if (!fileUrl) return '';
  const backendUrl = getApiBaseUrl();
  
  // Handle old absolute URLs - extract the path and use current backend
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    const apiMatch = fileUrl.match(/\/api\/(files|exports)\/(.+)/);
    if (apiMatch) {
      return `${backendUrl}/api/${apiMatch[1]}/${apiMatch[2]}`;
    }
    // Handle S3 URLs
    const s3Match = fileUrl.match(/https:\/\/([^.]+)\.s3(?:\.[^.]+)?\.amazonaws\.com\/([^?]+)/);
    if (s3Match) {
      return `${backendUrl}/api/s3-proxy/${s3Match[2]}`;
    }
    return fileUrl;
  }
  
  if (fileUrl.startsWith('/api/')) {
    return `${backendUrl}${fileUrl}`;
  }
  return `${backendUrl}/api/${fileUrl.replace(/^\//, '')}`;
};

export default function Storyboard() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [project, setProject] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  
  // Scene drag-and-drop state
  const [draggedSceneIndex, setDraggedSceneIndex] = useState(null);
  const [dragOverSceneIndex, setDragOverSceneIndex] = useState(null);
  
  // Rename dialog state
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameScene, setRenameScene] = useState(null);
  const [newSceneName, setNewSceneName] = useState("");
  
  // Guidance modal for empty state
  const [showGuidance, setShowGuidance] = useState(false);
  
  // Status filter
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Multi-select for batch publish
  const [selectedScenes, setSelectedScenes] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  
  // Batch publish state
  const [batchPublishing, setBatchPublishing] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ completed: 0, total: 0 });
  const batchPollRef = useRef(null);
  
  // Revision dialog
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionScene, setRevisionScene] = useState(null);
  const [revisionNotes, setRevisionNotes] = useState("");

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [projectRes, scenesRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/projects/${projectId}/scenes`, { headers: getAuthHeaders() })
      ]);
      setProject(projectRes.data);
      // Sort scenes by order, scene_number, or created_at
      const sortedScenes = (scenesRes.data || []).sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
        if (a.scene_number !== undefined && b.scene_number !== undefined) return a.scene_number - b.scene_number;
        return new Date(a.created_at) - new Date(b.created_at);
      });
      setScenes(sortedScenes);
      
      // Show guidance if no scenes
      if (sortedScenes.length === 0) {
        setShowGuidance(true);
      }
    } catch (error) {
      toast.error("Failed to load storyboard");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const getSceneThumbnail = (scene) => {
    // Priority order:
    // 1. First frame of printed video (thumbnail_url when scene has printed video)
    // 2. Composite image from Preview Step
    // 3. Canvas image (preview_url from "publish" action)
    // 4. Blank thumbnail (null)
    
    // Check if scene has a printed video (status is 'final' or has printed_take_id)
    const hasPrintedVideo = scene.status === 'final' || scene.printed_take_id;
    
    // If there's a printed video and thumbnail_url exists, it's likely the video first frame
    if (hasPrintedVideo && scene.thumbnail_url) {
      return getAssetUrl(scene.thumbnail_url);
    }
    
    // Composite image from Preview Step
    if (scene.composite_image_url) {
      return getAssetUrl(scene.composite_image_url);
    }
    
    // Canvas image from compose step (published preview)
    if (scene.preview_url) {
      return getAssetUrl(scene.preview_url);
    }
    
    // Fall back to thumbnail_url if it exists (even without printed video)
    if (scene.thumbnail_url) {
      return getAssetUrl(scene.thumbnail_url);
    }
    
    // No thumbnail available
    return null;
  };

  // Check if showing draft thumbnail (composite without final video)
  const isDraftThumbnail = (scene) => {
    const hasPrintedVideo = scene.status === 'final' || scene.printed_take_id;
    return !hasPrintedVideo && (scene.composite_image_url || scene.preview_url || scene.thumbnail_url);
  };

  // Get status display info
  const getStatusDisplay = (scene) => {
    const status = scene.status || 'draft';
    return STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  };

  // Filter scenes by status
  const filteredScenes = statusFilter === 'all' 
    ? scenes 
    : scenes.filter(s => (s.status || 'draft') === statusFilter);

  // Count scenes by status
  const statusCounts = scenes.reduce((acc, scene) => {
    const status = scene.status || 'draft';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  // Progress bar: X / Y shots printed (final)
  const finalCount = statusCounts.final || 0;
  const totalCount = scenes.length;
  const progressPercent = totalCount > 0 ? (finalCount / totalCount) * 100 : 0;

  // Get draft scenes for batch publish
  const draftScenes = scenes.filter(s => (s.status || 'draft') === 'draft' && s.composite_image_url);

  // Toggle scene selection
  const toggleSceneSelection = (sceneId) => {
    setSelectedScenes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sceneId)) {
        newSet.delete(sceneId);
      } else {
        newSet.add(sceneId);
      }
      return newSet;
    });
  };

  // Select all draft scenes
  const selectAllDrafts = () => {
    setSelectedScenes(new Set(draftScenes.map(s => s.id)));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedScenes(new Set());
    setSelectMode(false);
  };

  // Batch publish handler
  const handleBatchPublish = async (publishAll = false) => {
    const sceneIds = publishAll 
      ? draftScenes.map(s => s.id)
      : Array.from(selectedScenes).filter(id => draftScenes.some(s => s.id === id));
    
    if (sceneIds.length === 0) {
      toast.error("No draft scenes to publish");
      return;
    }

    try {
      setBatchPublishing(true);
      setBatchProgress({ completed: 0, total: sceneIds.length });
      
      const response = await axios.post(
        `${API}/projects/${projectId}/scenes/batch-publish`,
        sceneIds,
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        const batchId = response.data.data.batch_id;
        toast.success(`Publishing ${sceneIds.length} scenes...`);
        
        // Poll for progress
        batchPollRef.current = setInterval(async () => {
          try {
            const statusRes = await axios.get(
              `${API}/projects/${projectId}/batch-publish/${batchId}/status`,
              { headers: getAuthHeaders() }
            );
            
            const job = statusRes.data.data;
            setBatchProgress({ completed: job.completed, total: job.total });
            
            if (job.status === 'complete' || job.status === 'error') {
              clearInterval(batchPollRef.current);
              setBatchPublishing(false);
              clearSelection();
              
              if (job.status === 'complete') {
                toast.success(`Published ${job.completed} scenes!`);
              } else {
                toast.error(`Publish completed with ${job.failed} failures`);
              }
              
              // Refresh scenes
              fetchData();
            }
          } catch (error) {
            console.error("Polling error:", error);
          }
        }, 1000);
      }
    } catch (error) {
      toast.error("Failed to start batch publish");
      setBatchPublishing(false);
    }
  };

  // Flag for revision handler
  const openRevisionDialog = (e, scene) => {
    e.stopPropagation();
    setRevisionScene(scene);
    setRevisionNotes(scene.revision_notes || "");
    setRevisionDialogOpen(true);
  };

  const handleFlagRevision = async () => {
    if (!revisionScene) return;
    
    try {
      await axios.post(
        `${API}/scenes/${revisionScene.id}/flag-revision`,
        { notes: revisionNotes },
        { headers: getAuthHeaders() }
      );
      toast.success("Scene flagged for revision");
      setRevisionDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to flag scene");
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (batchPollRef.current) {
        clearInterval(batchPollRef.current);
      }
    };
  }, []);

  // Scene drag-and-drop handlers
  const handleSceneDragStart = (e, index) => {
    setDraggedSceneIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleSceneDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverSceneIndex !== index) {
      setDragOverSceneIndex(index);
    }
  };

  const handleSceneDragLeave = () => {
    setDragOverSceneIndex(null);
  };

  const handleSceneDrop = async (e, dropIndex) => {
    e.preventDefault();
    setDragOverSceneIndex(null);
    
    if (draggedSceneIndex === null || draggedSceneIndex === dropIndex) {
      setDraggedSceneIndex(null);
      return;
    }

    // Reorder scenes locally
    const newScenes = [...scenes];
    const [draggedScene] = newScenes.splice(draggedSceneIndex, 1);
    newScenes.splice(dropIndex, 0, draggedScene);
    setScenes(newScenes);
    setDraggedSceneIndex(null);

    // Save new order to backend
    try {
      const sceneIds = newScenes.map(s => s.id);
      await axios.put(`${API}/projects/${projectId}/scenes/reorder`, sceneIds, { headers: getAuthHeaders() });
      toast.success("Shot order updated");
    } catch (error) {
      // Revert on error
      fetchData();
      toast.error("Failed to reorder scenes");
    }
  };

  const handleSceneDragEnd = () => {
    setDraggedSceneIndex(null);
    setDragOverSceneIndex(null);
  };

  const openRenameDialog = (e, scene) => {
    e.stopPropagation();
    setRenameScene(scene);
    setNewSceneName(scene.name);
    setRenameDialogOpen(true);
  };

  const handleRenameScene = async () => {
    if (!renameScene || !newSceneName.trim()) return;
    
    try {
      await axios.put(`${API}/scenes/${renameScene.id}`, { name: newSceneName.trim() }, { headers: getAuthHeaders() });
      setScenes(scenes.map(s => 
        s.id === renameScene.id ? { ...s, name: newSceneName.trim() } : s
      ));
      toast.success("Shot renamed");
      setRenameDialogOpen(false);
      setRenameScene(null);
    } catch (error) {
      toast.error("Failed to rename scene");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] animate-fadeIn">
      {/* Guidance Modal for empty state */}
      <GuidanceModal
        open={showGuidance}
        onClose={() => setShowGuidance(false)}
        type="no-shots"
        projectId={projectId}
      />
      
      {/* Revision Dialog */}
      <Dialog open={revisionDialogOpen} onOpenChange={setRevisionDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Flag for Revision</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-zinc-400">
              Add notes explaining what needs to be revised for &quot;{revisionScene?.name}&quot;
            </p>
            <Textarea
              value={revisionNotes}
              onChange={(e) => setRevisionNotes(e.target.value)}
              placeholder="Describe what needs to be changed..."
              className="bg-zinc-800 border-zinc-700 min-h-[100px]"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRevisionDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleFlagRevision} className="bg-orange-500 hover:bg-orange-600">
              <Flag className="w-4 h-4 mr-2" />
              Flag for Revision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/project/${projectId}`)}
                className="text-zinc-400 hover:text-white"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Film className="w-5 h-5 text-indigo-400" />
                  Storyboard
                </h1>
                <p className="text-sm text-zinc-400">{project?.name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px] bg-zinc-800 border-zinc-700 text-white">
                  <Filter className="w-4 h-4 mr-2 text-zinc-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">All ({scenes.length})</SelectItem>
                  <SelectItem value="draft">Draft ({statusCounts.draft || 0})</SelectItem>
                  <SelectItem value="ready">Ready ({statusCounts.ready || 0})</SelectItem>
                  <SelectItem value="animating">Animating ({statusCounts.animating || 0})</SelectItem>
                  <SelectItem value="animated">Animated ({statusCounts.animated || 0})</SelectItem>
                  <SelectItem value="needs_revision">Revision ({statusCounts.needs_revision || 0})</SelectItem>
                  <SelectItem value="final">Final ({statusCounts.final || 0})</SelectItem>
                </SelectContent>
              </Select>
              
              {/* View Toggle */}
              <div className="flex items-center bg-zinc-800 rounded-lg p-1">
                <Button
                  variant={viewMode === "grid" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("grid")}
                  className={viewMode === "grid" ? "bg-indigo-500" : "text-zinc-400"}
                >
                  <Grid3X3 className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className={viewMode === "list" ? "bg-indigo-500" : "text-zinc-400"}
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Progress Bar & Batch Publish */}
          <div className="flex items-center justify-between gap-4">
            {/* Progress: X / Y shots printed */}
            <div className="flex-1 max-w-md">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-zinc-400">{finalCount} / {totalCount} shots printed</span>
                <span className="text-yellow-400 font-medium">{Math.round(progressPercent)}%</span>
              </div>
              <Progress value={progressPercent} className="h-2 bg-zinc-800" />
            </div>
            
            {/* Batch Publish Controls */}
            <div className="flex items-center gap-2">
              {selectMode ? (
                <>
                  <span className="text-sm text-zinc-400">
                    {selectedScenes.size} selected
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={selectAllDrafts}
                    className="text-zinc-400 hover:text-white"
                  >
                    Select All Drafts
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSelection}
                    className="text-zinc-400 hover:text-white"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleBatchPublish(false)}
                    disabled={selectedScenes.size === 0 || batchPublishing}
                    className="bg-indigo-500 hover:bg-indigo-600"
                  >
                    {batchPublishing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Publish Selected</>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  {draftScenes.length > 0 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectMode(true)}
                        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      >
                        Select
                      </Button>
                      <Button
                        onClick={() => handleBatchPublish(true)}
                        disabled={batchPublishing}
                        className="bg-indigo-500 hover:bg-indigo-600"
                        data-testid="publish-all-drafts-btn"
                      >
                        {batchPublishing ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing...</>
                        ) : (
                          <><Send className="w-4 h-4 mr-2" /> Publish All Drafts ({draftScenes.length})</>
                        )}
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          
          {/* Batch Progress Bar */}
          {batchPublishing && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-zinc-400">Publishing scenes...</span>
                <span className="text-indigo-400">{batchProgress.completed} / {batchProgress.total}</span>
              </div>
              <Progress 
                value={(batchProgress.completed / batchProgress.total) * 100} 
                className="h-2 bg-zinc-800" 
              />
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {filteredScenes.length === 0 && statusFilter !== 'all' ? (
          <div className="text-center py-20">
            <Filter className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">No {statusFilter} shots</h2>
            <p className="text-zinc-400 mb-6">Try a different filter or view all shots</p>
            <Button onClick={() => setStatusFilter("all")} variant="outline">
              View All Shots
            </Button>
          </div>
        ) : scenes.length === 0 ? (
          <div className="text-center py-20">
            <Film className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">No scenes yet</h2>
            <p className="text-zinc-400 mb-6">Create scenes from the project editor to see them here</p>
            <Button
              onClick={() => navigate(`/project/${projectId}`)}
              className="bg-indigo-500 hover:bg-indigo-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Go to Project Editor
            </Button>
          </div>
        ) : viewMode === "grid" ? (
          /* Grid View - Storyboard Style */
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredScenes.map((scene, index) => {
              const statusDisplay = getStatusDisplay(scene);
              const StatusIcon = statusDisplay.icon;
              const isDraft = isDraftThumbnail(scene);
              const isSelected = selectedScenes.has(scene.id);
              
              return (
                <div
                  key={scene.id}
                  data-testid={`storyboard-scene-${index}`}
                  draggable={!selectMode}
                  onDragStart={(e) => !selectMode && handleSceneDragStart(e, index)}
                  onDragOver={(e) => !selectMode && handleSceneDragOver(e, index)}
                  onDragLeave={!selectMode ? handleSceneDragLeave : undefined}
                  onDrop={(e) => !selectMode && handleSceneDrop(e, index)}
                  onDragEnd={!selectMode ? handleSceneDragEnd : undefined}
                  onClick={() => selectMode ? toggleSceneSelection(scene.id) : navigate(`/project/${projectId}/studio/${scene.id}`)}
                  className={`group cursor-pointer transition-all ${
                    draggedSceneIndex === index ? 'opacity-50 scale-95' : ''
                  } ${isSelected ? 'ring-2 ring-indigo-500 rounded-lg' : ''}`}
                >
                  {/* Thumbnail */}
                  <div className={`aspect-video bg-zinc-800 rounded-lg overflow-hidden border-2 transition-colors relative ${
                    dragOverSceneIndex === index && draggedSceneIndex !== index
                      ? 'border-indigo-500 border-dashed'
                      : isSelected 
                        ? 'border-indigo-500'
                        : 'border-zinc-700 group-hover:border-indigo-500'
                  } ${isDraft ? 'opacity-80' : ''}`}>
                    {getSceneThumbnail(scene) ? (
                      <img
                        src={getSceneThumbnail(scene)}
                        alt={scene.name}
                        className={`w-full h-full object-cover ${isDraft ? 'saturate-50' : ''}`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Film className="w-8 h-8 text-zinc-600" />
                      </div>
                    )}
                    
                    {/* Select checkbox */}
                    {selectMode && (
                      <div className="absolute top-2 left-2 z-10">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSceneSelection(scene.id)}
                          className="h-5 w-5 border-white bg-black/50"
                        />
                      </div>
                    )}
                    
                    {/* Drag handle indicator */}
                    {!selectMode && (
                      <div className="absolute top-2 left-2 flex items-center gap-1">
                        <div className="w-6 h-6 rounded bg-black/70 text-white text-xs flex items-center justify-center font-bold">
                          {scenes.indexOf(scene) + 1}
                        </div>
                        <div className="w-5 h-5 rounded bg-black/50 text-zinc-400 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
                          <GripVertical className="w-3 h-3" />
                        </div>
                      </div>
                    )}
                    
                    {/* Status badge */}
                    {(!statusDisplay.hidden || isDraft || scene.status === 'needs_revision' || scene.status === 'final') && (
                      <div className={`absolute top-2 right-2 px-2 py-0.5 rounded text-[10px] font-medium flex items-center gap-1 ${statusDisplay.color} text-white`}>
                        {statusDisplay.spinner && <Loader2 className="w-3 h-3 animate-spin" />}
                        {StatusIcon && <StatusIcon className="w-3 h-3" />}
                        {statusDisplay.label}
                      </div>
                    )}
                    
                    {/* Revision notes indicator */}
                    {scene.status === 'needs_revision' && scene.revision_notes && (
                      <div className="absolute bottom-2 right-2">
                        <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center" title={scene.revision_notes}>
                          <AlertTriangle className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    )}
                    
                    {/* Play overlay on hover */}
                    {!selectMode && (
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Play className="w-10 h-10 text-white" />
                      </div>
                    )}
                  </div>
                  
                  {/* Shot info */}
                  <div className="mt-2 px-1">
                    <div className="flex items-center justify-between gap-1">
                      <h3 
                        className="text-sm font-medium text-white truncate flex-1"
                        title={scene.name.length > 15 ? scene.name : undefined}
                      >
                        {scene.name.length > 15 ? `${scene.name.substring(0, 15)}...` : scene.name}
                      </h3>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => openRevisionDialog(e, scene)}
                          className="p-1 text-zinc-500 hover:text-orange-400"
                          title="Flag for revision"
                        >
                          <Flag className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => openRenameDialog(e, scene)}
                          className="p-1 text-zinc-500 hover:text-white"
                          title="Rename shot"
                        >
                          <Edit3 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-zinc-500 truncate">
                      {scene.script_segment?.substring(0, 40)}...
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* List View */
          <div className="space-y-3">
            {filteredScenes.map((scene, index) => {
              const statusDisplay = getStatusDisplay(scene);
              const StatusIcon = statusDisplay.icon;
              const isDraft = isDraftThumbnail(scene);
              const isSelected = selectedScenes.has(scene.id);
              
              return (
                <div
                  key={scene.id}
                  data-testid={`storyboard-scene-list-${index}`}
                  draggable={!selectMode}
                  onDragStart={(e) => !selectMode && handleSceneDragStart(e, index)}
                  onDragOver={(e) => !selectMode && handleSceneDragOver(e, index)}
                  onDragLeave={!selectMode ? handleSceneDragLeave : undefined}
                  onDrop={(e) => !selectMode && handleSceneDrop(e, index)}
                  onDragEnd={!selectMode ? handleSceneDragEnd : undefined}
                  onClick={() => selectMode ? toggleSceneSelection(scene.id) : navigate(`/project/${projectId}/studio/${scene.id}`)}
                  className={`group flex items-center gap-4 p-4 bg-zinc-900/50 rounded-lg border-2 cursor-pointer transition-all ${
                    draggedSceneIndex === index ? 'opacity-50 scale-[0.98]' : ''
                  } ${
                    dragOverSceneIndex === index && draggedSceneIndex !== index
                      ? 'border-indigo-500 border-dashed'
                      : isSelected
                        ? 'border-indigo-500 bg-indigo-500/10'
                        : 'border-zinc-800 hover:border-indigo-500'
                  }`}
                >
                  {/* Select checkbox or Drag handle */}
                  {selectMode ? (
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSceneSelection(scene.id)}
                      className="h-5 w-5"
                    />
                  ) : (
                    <div className="text-zinc-600 hover:text-zinc-400 flex-shrink-0 cursor-grab active:cursor-grabbing">
                      <GripVertical className="w-5 h-5" />
                    </div>
                  )}
                  
                  {/* Thumbnail */}
                  <div className={`w-32 h-20 bg-zinc-800 rounded overflow-hidden flex-shrink-0 relative ${isDraft ? 'opacity-80' : ''}`}>
                    {getSceneThumbnail(scene) ? (
                      <img
                        src={getSceneThumbnail(scene)}
                        alt={scene.name}
                        className={`w-full h-full object-cover ${isDraft ? 'saturate-50' : ''}`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Film className="w-6 h-6 text-zinc-600" />
                      </div>
                    )}
                    <div className="absolute top-1 left-1 w-5 h-5 rounded bg-black/70 text-white text-xs flex items-center justify-center font-bold">
                      {scenes.indexOf(scene) + 1}
                    </div>
                    {isDraft && (
                      <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-zinc-600 text-white text-[8px] font-medium">
                        Draft
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 
                        className="font-medium text-white"
                        title={scene.name.length > 15 ? scene.name : undefined}
                      >
                        {scene.name.length > 15 ? `${scene.name.substring(0, 15)}...` : scene.name}
                      </h3>
                      <button
                        onClick={(e) => openRenameDialog(e, scene)}
                        className="p-1 text-zinc-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Rename scene"
                      >
                        <Edit3 className="w-3 h-3" />
                      </button>
                      {(!statusDisplay.hidden || scene.status === 'needs_revision' || scene.status === 'final' || scene.status === 'draft') && (
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium flex items-center gap-1 ${statusDisplay.color} text-white`}>
                          {statusDisplay.spinner && <Loader2 className="w-3 h-3 animate-spin" />}
                          {StatusIcon && <StatusIcon className="w-3 h-3" />}
                          {statusDisplay.label}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-400 mt-1 line-clamp-2">
                      {scene.script_segment}
                    </p>
                    {scene.status === 'needs_revision' && scene.revision_notes && (
                      <p className="text-xs text-orange-400 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {scene.revision_notes}
                      </p>
                    )}
                  </div>
                
                {/* Action */}
                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-zinc-400 hover:text-orange-400"
                    onClick={(e) => openRevisionDialog(e, scene)}
                    title="Flag for revision"
                  >
                    <Flag className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-zinc-400 group-hover:text-indigo-400"
                    onClick={(e) => { e.stopPropagation(); navigate(`/project/${projectId}/studio/${scene.id}`); }}
                  >
                    <Play className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Rename Shot Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>Rename Shot</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newSceneName}
              onChange={(e) => setNewSceneName(e.target.value)}
              placeholder="Shot name"
              className="bg-zinc-800 border-zinc-700 text-white"
              onKeyDown={(e) => e.key === 'Enter' && handleRenameScene()}
              data-testid="rename-scene-input"
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                Cancel
              </Button>
            </DialogClose>
            <Button 
              onClick={handleRenameScene}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="rename-scene-submit"
            >
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
